import { createSelector } from "@reduxjs/toolkit"

/* One shared empty array for the "not loaded yet" answer.

   `state.economy.thing || []` looks harmless and is not: it builds a *new* array every call, and
   `useSelector` compares by reference, so every render sees a change and schedules another one —
   React gives up with "Maximum update depth exceeded". Returning the same object every time is
   what makes the empty case stable. */
const NOTHING = Object.freeze([])

export const selectCredits = (state) => state.economy.credits
export const selectDaily = (state) => state.economy.daily
export const selectGameEntryCost = (state) => state.economy.gameEntryCost
export const selectCreditHistory = (state) => state.economy.history
export const selectUnlockedRaces = (state) => state.economy.unlockedRaces

// Defaults to empty: the shop's catalogue is only read when the shop is opened, and every
// screen that asks before then should render nothing rather than fall over.
export const selectCosmetics = (state) => state.economy.cosmetics || NOTHING

// What the player *holds*, which arrives with the wallet and so is known on the game screen
// without the shop ever having been opened.
export const selectOwnedCosmetics = (state) => state.economy.ownedCosmetics || NOTHING

export const selectRaceCatalogue = (state) => state.economy.races
export const selectCreditPacks = (state) => state.economy.packs
export const selectCurrency = (state) => state.economy.currency
export const selectPurchasesEnabled = (state) => state.economy.purchasesEnabled
export const selectShopLoaded = (state) => state.economy.shopLoaded

export const selectDailyClaimable = createSelector(selectDaily, (daily) => Boolean(daily?.claimable))

// Whether the player can pay for a seat. `null` credits means the wallet has not been read yet,
// and an unread wallet must not read as "cannot afford" — the lobby would tell somebody with
// plenty that they are broke for as long as the first request takes.
//
// A member's cost is 0, so this is true for them without a special case here: the server sends
// *this player's* entry cost rather than the catalogue's, which is the whole reason it does.
export const selectCanAffordGame = createSelector(
  selectCredits,
  selectGameEntryCost,
  (credits, cost) => credits === null || cost === null || credits >= cost
)

// One race at a time, for the lobby's cards. Unknown while the wallet is unread, which the cards
// render as "loading" rather than as "locked".
export const selectRaceIsUnlocked = createSelector(
  selectUnlockedRaces,
  (unlocked) => (race) => unlocked.includes(race)
)

// What the player is wearing, as the wallet last answered it (`equipCosmetic` writes it). Null
// until a look is equipped in this session — readers fall back to the game payload's copy.
export const selectEquippedCosmetics = (state) => state.economy.equippedCosmetics || null

// The player's own picture: the wallet's answer, or the free face a fresh session falls back to.
export const selectMyAvatar = (state) => {
  const worn = state.economy?.equippedCosmetics?.avatar
  return typeof worn === "string" ? worn : null
}
